<template>
  <div class="tutorial-container" v-if="isOpen" @click.self="close">
    <div class="tutorial-modal">
      
      <div class="modal-header">
        <h3 class="modal-title">{{ title }}</h3>
        <button class="btn-back" id="closeBtn" type="button" name="button" data-test="btnClose" aria-label="Close" @click="close">
          {{ $t('close') }}
        </button>
      </div>

      <div class="modal-body" v-if="currentStepData">
        
        <div class="step-content">
          <img :src="currentStepData.image" :alt="currentStepData.title" class="step-image">
          <h4 class="step-title" v-html="currentStepData.title"></h4>
          <p class="step-text" v-html="currentStepData.text"></p>
          
          <p v-if="currentStepData.warning" class="step-text text-danger small-alert" v-html="currentStepData.warning"></p>
        </div>

        <div class="step-indicators">
          <span v-for="n in steps.length" :key="n" class="dot" :class="{ active: tutorialStep === n }"></span>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-outline-secondary" :class="{ 'invisible': tutorialStep === 1 }" type="button" data-test="btnPrevious" @click="prevStep">
          {{ $t('pagination.previous')}}
        </button>
        
        <button v-if="tutorialStep < steps.length" class="btn btn-success" type="button" data-test="btnNext" @click="nextStep">
          {{ $t('pagination.next')}}
        </button>
        <button v-else class="btn btn-success" type="button" data-test="btnFinish" @click="close">
            {{ $t('pagination.finish')}}
        </button>
      </div>

    </div>
  </div>
</template>

<style lang="scss" scoped>
.tutorial-container {
  position:                             fixed;
  top:                                  0;
  left:                                 0;
  width:                                100vw;
  height:                               100vh;
  background:                           rgba(0, 0, 0, 0.6);
  display:                              flex;
  align-items:                          center;
  justify-content:                      center;
  z-index:                              9999;
}

.tutorial-modal {
  // background:                           var(--Shades-W, #ffffff);
  background-color:                     var(--Shades-BG);
  width:                                100%;
  max-width:                            700px;
  border-radius:                        10px;
  display:                              flex;
  flex-direction:                       column;
  box-shadow:                           0 10px 30px rgba(0, 0, 0, 0.2);
  animation:                            modalPop 0.3s ease-out;
}

.modal-header {
  display:                              flex;
  justify-content:                      space-between;
  align-items:                          center;
  padding:                              20px 25px;
  border-bottom:                        1px solid var(--Shades-30-Border, #e0e0e0);

  .modal-title {
    margin:                             0;
    color:                              var(--Brand-Primary);
    font-size:                          var(--font-size-20);
    text-transform:                     uppercase;
  }
}

.modal-body {
  padding:                              30px 25px;
  display:                              flex;
  flex-direction:                       column;
  align-items:                          center;
}

.step-content {
  display:                              flex;
  flex-direction:                       column;
  align-items:                          center;
  text-align:                           center;
  animation:                            fadeIn 0.3s ease-in-out;
  width:                                100%;

  .step-image {
    width:                              100%;
    max-width:                          500px;
    height:                             250px;
    object-fit:                         contain;
    background:                         var(--Shades-10);
    border:                             1px solid var(--Shades-30-Border);
    border-radius:                      8px;
    padding:                            10px;
    margin-bottom:                      20px;
  }

  .step-title {
    color:                              var(--Brand-Primary);
    margin:                             0 0 10px 0;
    font-size:                          var(--font-size-18);
    font-weight:                        600;
  }

  .step-text {
    color:                              var(--Brand-Primary);
    font-size:                          var(--font-size-16);
    line-height:                        1.5;
    margin:                             0;
    max-width:                          620px;
  }
}

:deep(.tag-highlight) {
  background:                           var(--Shades-10);
  padding:                              2px 6px;
  border-radius:                        4px;
  border:                               1px solid var(--Shades-30-Border);
}

:deep(.text-danger) {
  color:                                #dc3545;
}

.small-alert {
  font-size:                            var(--font-size-14) !important;
  font-weight:                          600;
  display:                              block;
  margin-top:                           8px;
}

.step-indicators {
  display:                              flex;
  gap:                                  10px;
  margin-top:                           30px;

  .dot {
    width:                              10px;
    height:                             10px;
    border-radius:                      50%;
    background:                         var(--Shades-W, #FFF);
    border:                             1px solid var(--Brand-Secondary, #3CB391);
    transition:                         0.3s ease;

    &.active {
      background:                       var(--Brand-Secondary, #3CB391);
      transform:                        scale(1.2);
    }
  }
}

.modal-footer {
  padding:                              20px 25px;
  display:                              flex;
  justify-content:                      space-between;
  border-top:                           1px solid var(--Shades-30-Border);

  button {
    font-size:                          var(--font-size-12);
    font-weight:                        500;
    line-height:                        normal;
    letter-spacing:                     2.58px;
    text-transform:                     uppercase;
    padding:                            10px;
  }
}

@keyframes modalPop {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>

<script>
export default {
  name: 'BaseTutorialModal',
  props: {
    title: {
      type: String,
      required: true
    },
    steps: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data: () => ({
    isOpen: false,
    tutorialStep: 1,
  }),
  computed: {
    currentStepData() {
      return this.steps[this.tutorialStep - 1];
    }
  },
  methods: {
    show() {
      this.tutorialStep = 1;
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
    },
    nextStep() {
      if (this.tutorialStep < this.steps.length) this.tutorialStep++;
    },
    prevStep() {
      if (this.tutorialStep > 1) this.tutorialStep--;
    }
  }
}
</script>